/*
  Rosie welcome credit + bag progress (v2).

  How it works, in one paragraph:
  1. A visitor signs up in the popup (Klaviyo). Only after Klaviyo accepts the signup do we remember "claimed".
  2. While claimed, we keep two Pixoo discount CODES on the Shopify cart: the welcome credit and the
     second-item bonus. A small reconciler re-adds them whenever they go missing, so the credit can't
     silently disappear. Pixoo/Shopify does the actual discounting, in the shopper's checkout currency.
  3. The bag card is drawn from the last known cart. It is never blanked while it refreshes, and it repaints
     itself if the theme re-renders the drawer.

  No server, no private keys. Nothing here creates a discount; it only applies codes you made in Pixoo.
*/
(() => {
  'use strict';
  if (window.RosieCredit) return;
  const root = document.getElementById('rosie-welcome-credit');
  if (!root) return;
  let config;
  try { config = JSON.parse(root.querySelector('[data-credit-config]').textContent); } catch { return; }

  /* ---------- environment ---------- */
  const params = new URLSearchParams(location.search);
  const editor = Boolean(config.designMode || window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
  const forcedPreview = params.get('rosie_credit_preview') === '1';
  const preview = config.mode === 'preview' || forcedPreview;
  const themePreview = editor || ['unpublished', 'development'].includes(window.Shopify?.theme?.role) || params.has('preview_theme_id') || location.hostname.endsWith('.shopifypreview.com');
  // Preview mode is invisible to the public; only the theme editor / unpublished preview sees it.
  if (config.mode === 'off' || (preview && !themePreview && !forcedPreview)) return;

  const nativeFetch = window.fetch.bind(window);
  const q = s => root.querySelector(s);
  const qa = s => root.querySelectorAll(s);
  const lower = s => String(s || '').trim().toLowerCase();

  /* ---------- markets & money ---------- */
  const previewCountries = { GB: ['GBP', 'United Kingdom'], US: ['USD', 'United States'], CA: ['CAD', 'Canada'] };
  const symbols = { GBP: '£', USD: 'US$', EUR: '€', CAD: 'CA$', AUD: 'AU$', NZD: 'NZ$' };
  let currency = String(config.currency || '').toUpperCase();
  let country = config.country;
  let countryName = config.countryName;
  if (preview && previewCountries[config.previewCountry]) {
    currency = previewCountries[config.previewCountry][0];
    countryName = previewCountries[config.previewCountry][1];
    country = config.previewCountry;
  }
  const cleanMarket = value => {
    const welcome = Number(value?.welcome), bonus = Number(value?.bonus);
    return Number.isFinite(welcome) && welcome > 0 ? { welcome, bonus: Number.isFinite(bonus) && bonus > 0 ? bonus : 0 } : null;
  };
  const markets = {};
  for (const [code, value] of Object.entries(config.markets || {})) { const m = cleanMarket(value); if (m) markets[code.toUpperCase()] = m; }
  try {
    const extra = JSON.parse(config.extraMarkets || '{}');
    if (extra && typeof extra === 'object' && !Array.isArray(extra)) {
      for (const [code, value] of Object.entries(extra)) { const m = cleanMarket(value); if (m && /^[A-Za-z]{3}$/.test(code)) markets[code.toUpperCase()] = m; }
    }
  } catch { /* ignore malformed optional JSON */ }
  // No configured amount for this currency = no offer. We never promise what Pixoo isn't set up to give.
  const market = markets[currency] || null;
  const bonusOn = Boolean(market && market.bonus > 0 && config.bonusEnabled && config.codes.bonus);
  const MIN_ITEMS = 2;

  function format(amount, code = currency) {
    if (!Number.isFinite(amount) || amount < 0 || !/^[A-Z]{3}$/.test(code)) return '';
    const number = new Intl.NumberFormat('en-GB', { minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 }).format(amount);
    if (symbols[code]) return symbols[code] + number;
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: code, minimumFractionDigits: 0 }).format(amount); } catch { return number + ' ' + code; }
  }

  /* ---------- persistent state (never throws, falls back to memory) ---------- */
  const ns = 'rosie-credit-v2:' + (preview ? 'preview' : 'live');
  let backend = null;
  try { backend = preview ? window.sessionStorage : window.localStorage; backend.getItem(ns); } catch { backend = null; }
  let state = {};
  const load = () => { try { state = backend ? JSON.parse(backend.getItem(ns) || '{}') || {} : state; } catch { state = state || {}; } };
  const save = () => { try { backend?.setItem(ns, JSON.stringify(state)); } catch { /* memory copy still works */ } };
  load();
  if (preview && (editor || forcedPreview)) { state = {}; save(); }
  const claimed = () => Boolean(state.claimedAt);
  const DISMISS_DAYS = 3;
  const dismissedRecently = () => Boolean(state.dismissedAt) && Date.now() - state.dismissedAt < DISMISS_DAYS * 864e5;

  /* ---------- ad-visitor detection (remembered for this browsing session) ---------- */
  const isAdVisitor = () => {
    try { if (sessionStorage.getItem('rosie-credit-ad') === '1') return true; } catch { /* ignore */ }
    const clickIds = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid', 'epik', 'twclid'];
    const medium = params.get('utm_medium') || '';
    const hit = clickIds.some(id => params.has(id)) || /(^|[-_ ])(paid|cpc|ppc|cpm|display|ads?)([-_ ]|$)/i.test(medium);
    if (hit) { try { sessionStorage.setItem('rosie-credit-ad', '1'); } catch { /* ignore */ } }
    return hit;
  };
  isAdVisitor();

  /* ---------- cart snapshot (stale-while-revalidate: never cleared, only replaced) ---------- */
  let snapshot = null;
  const cards = new Set();
  function accept(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    snapshot = {
      currency: cart.currency || currency,
      count: cart.items.reduce((total, item) => total + (item.gift_card ? 0 : Number(item.quantity) || 0), 0),
      hasCodes: Array.isArray(cart.discount_codes),
      codes: (cart.discount_codes || []).map(entry => ({ code: String(entry.code || ''), applicable: entry.applicable }))
    };
    paintCards();
  }
  async function cartRequest(path, body) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await nativeFetch((window.Shopify?.routes?.root || '/') + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  }
  // One cart operation at a time, so two updates can never overwrite each other's discount list.
  let chain = Promise.resolve();
  const serial = fn => (chain = chain.then(fn, fn));

  /* ---------- the reconciler: keep the credit codes on the cart ---------- */
  // Only *refusals* count against the retry limit: a code Shopify legitimately dropped after a cart change
  // is simply re-added every time. If Shopify keeps ignoring our update, we stop after two tries.
  const refusals = new Map();
  const wantedCodes = () => [config.codes.welcome, bonusOn && config.codes.bonus].filter(Boolean);
  async function reconcile(cart) {
    if (preview || !market || !claimed() || !cart || !Array.isArray(cart.items)) return 'skipped';
    const have = (cart.discount_codes || []).map(entry => lower(entry.code));
    const missing = wantedCodes().filter(code => !have.includes(lower(code)));
    if (!missing.length) { refusals.clear(); return 'ok'; }
    const key = missing.map(lower).join('|');
    const refused = refusals.get(key) || { n: 0, t: 0 };
    if (refused.n >= 2 && Date.now() - refused.t < 30000) return 'failed';
    const existing = (cart.discount_codes || []).map(entry => entry.code);
    const updated = await cartRequest('cart/update.js', { discount: [...existing, ...missing].join(',') });
    accept(updated);
    const kept = (updated.discount_codes || []).map(entry => lower(entry.code));
    if (missing.every(code => kept.includes(lower(code)))) refusals.delete(key);
    else refusals.set(key, { n: refused.n + 1, t: Date.now() });
    // The full cart page is rendered by the server, so it needs one reload to show a newly added discount.
    if (config.pageType === 'cart') {
      let last = 0;
      try { last = Number(sessionStorage.getItem('rosie-credit-reload')) || 0; } catch { /* ignore */ }
      if (Date.now() - last > 30000) { try { sessionStorage.setItem('rosie-credit-reload', String(Date.now())); } catch { /* ignore */ } location.reload(); }
    }
    return 'applied';
  }
  let syncTimer;
  const sync = () => serial(async () => {
    try { const cart = await cartRequest('cart.js'); accept(cart); await reconcile(cart); } catch { /* keep the last known snapshot on screen */ }
  });
  const scheduleSync = (delay = 120) => { clearTimeout(syncTimer); syncTimer = setTimeout(sync, delay); };

  // Catch every cart change however the theme makes it (product drawer, header bag, quick add, cart page).
  if (!window.__rosieCreditFetchPatched) {
    window.__rosieCreditFetchPatched = true;
    const mutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    window.fetch = function (input, init) {
      const result = nativeFetch.apply(window, arguments);
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (mutation.test(url)) result.then(() => scheduleSync(150), () => {});
      } catch { /* never interfere with the theme's own request */ }
      return result;
    };
  }

  /* ---------- the bag card ---------- */
  function cardModel() {
    if (!market || !claimed() || !snapshot) return null;
    if (!preview && snapshot.currency !== currency) return null;
    const count = snapshot.count;
    if (count < 1) return null;
    const welcome = market.welcome, bonus = bonusOn ? market.bonus : 0;
    const full = bonusOn && count >= MIN_ITEMS;
    const entry = code => snapshot.codes.find(item => lower(item.code) === lower(code));
    const live = !preview && snapshot.hasCodes;
    const welcomeFailed = live && entry(config.codes.welcome)?.applicable === false;
    const bonusFailed = live && full && entry(config.codes.bonus)?.applicable === false;
    const saved = welcomeFailed ? 0 : welcome + (full && !bonusFailed ? bonus : 0);
    return { count, welcome, bonus, target: welcome + bonus, full: full && !bonusFailed && !welcomeFailed, saved, welcomeFailed, bonusFailed };
  }
  const setText = (el, text) => { if (el && el.textContent !== text) el.textContent = text; };
  const setAttr = (el, name, value) => { if (el && el.getAttribute(name) !== value) el.setAttribute(name, value); };

  class RosieCartCreditProgress extends HTMLElement {
    connectedCallback() {
      cards.add(this);
      this.paint();
      // Self-heal: if the theme morphs this element back to its server HTML, repaint from the cached cart.
      this.observer = new MutationObserver(() => { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.paint(); }); });
      this.observer.observe(this, { attributes: true, attributeFilter: ['hidden', 'data-complete'], childList: true, characterData: true, subtree: true });
      if (!snapshot) scheduleSync(0);
    }
    disconnectedCallback() { cards.delete(this); this.observer?.disconnect(); cancelAnimationFrame(this.raf || 0); this.raf = 0; }
    paint() {
      const model = cardModel();
      if (!model) { if (!this.hidden) this.hidden = true; return; }
      const get = name => this.querySelector('[data-rcc-' + name + ']');
      const fmt = amount => format(amount);
      const { welcome, bonus, target, saved, full, count } = model;
      let headline, detail;
      if (model.welcomeFailed) {
        headline = 'Your welcome credit isn’t applying yet.';
        detail = 'We couldn’t apply it to this bag automatically. You can enter code ' + config.codes.welcome + ' at checkout, or message us and we’ll sort it.';
      } else if (model.bonusFailed) {
        headline = 'Your second-item saving isn’t applied yet.';
        detail = 'Your bag has two items, but the extra ' + fmt(bonus) + ' hasn’t applied. You can enter code ' + config.codes.bonus + ' at checkout.';
      } else if (full) {
        headline = 'Lovely choice. Your extra ' + fmt(bonus) + ' is applied!';
        detail = fmt(saved) + ' off this order, automatically. Applied as discounts at checkout.';
      } else if (!bonusOn) {
        headline = 'Your welcome credit is applied.';
        detail = fmt(welcome) + ' off this order. Applied as a one-use discount at checkout.';
      } else {
        headline = 'Add a second item for an extra ' + fmt(bonus) + ' off.';
        detail = 'Your ' + fmt(welcome) + ' welcome credit + ' + fmt(bonus) + ' extra = ' + fmt(target) + ' off together. Two items required; applied as discounts.';
      }
      const percentage = Math.min(100, Math.max(0, saved / target * 100));
      const track = get('track');
      if (this.hidden) this.hidden = false;
      setAttr(this, 'data-complete', String(full));
      const previewNote = get('preview'); if (previewNote) previewNote.hidden = !preview;
      setText(get('headline'), headline);
      setText(get('amount'), fmt(saved) + (bonusOn ? ' / ' + fmt(target) : ''));
      setText(get('start'), fmt(welcome) + ' welcome');
      setText(get('goal'), bonusOn ? fmt(target) + ' together' : 'Welcome offer');
      setText(get('detail'), detail);
      setAttr(track, 'aria-valuenow', String(Math.round(percentage)));
      setAttr(track, 'aria-valuetext', fmt(saved) + ' applied, ' + fmt(target) + ' available');
      const fill = get('fill'); const width = percentage + '%'; if (fill && fill.style.width !== width) fill.style.width = width;
      const shop = get('shop');
      if (shop) {
        const hide = !bonusOn || count >= MIN_ITEMS;
        if (shop.hidden !== hide) shop.hidden = hide;
        shop.onclick = () => { window.HbrSharedBag?.close?.(); document.getElementById('cart-drawer')?.close?.(); };
      }
      const announcement = headline + ' ' + fmt(saved) + ' applied.';
      if (this.lastAnnouncement !== announcement) { this.lastAnnouncement = announcement; setText(get('live'), announcement); }
    }
  }
  if (!customElements.get('rosie-cart-credit-progress')) customElements.define('rosie-cart-credit-progress', RosieCartCreditProgress);
  const paintCards = () => cards.forEach(card => card.paint());

  // Public API the theme's drawers already call. Passing the full /cart.js object is enough.
  window.RosieCartCredit = Object.freeze({
    update(cart) { accept(cart); serial(() => reconcile(cart)).catch(() => {}); },
    refresh: () => scheduleSync(0)
  });
  window.RosieWelcomeCredit = Object.freeze({ getState: () => (market && claimed() ? Object.freeze({ amount: market.welcome, currency, preview }) : null) });

  for (const name of ['rosie:bag:open', 'theme-drawer:open', 'shopify:cart:lines-update', 'shopify:cart:discount-update']) document.addEventListener(name, () => scheduleSync(60));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleSync(0); });
  window.addEventListener('pageshow', event => { if (event.persisted) { load(); paintBalance(); scheduleSync(0); } });
  window.addEventListener('storage', event => { if (event.key === ns) { load(); paintBalance(); paintCards(); scheduleSync(0); } });

  /* ---------- the popup ---------- */
  const dialog = q('dialog'), form = q('form'), email = q('[name=email]'), consent = q('[name=marketing_consent]');
  const steps = { signup: q('[data-credit-signup]'), success: q('[data-credit-success]'), balance: q('[data-credit-balance-view]') };
  const status = q('[data-credit-error]'), live = q('[data-credit-live]');
  const klaviyo = window.RosieKlaviyoSignup;
  // A signup we can't complete must never be advertised.
  const signupReady = Boolean(market) && (preview || Boolean(klaviyo?.configured(config.klaviyo)));
  let autoTimer, toastTimer, previousFocus, busy = false;
  const setStatus = text => { status.textContent = text; };
  const buttons = () => [...document.querySelectorAll('[data-rc-balance]')];

  const paintAmount = () => qa('[data-credit-amount]').forEach(el => {
    const label = format(market.welcome);
    if (el.classList.contains('credit-value')) {
      const split = label.match(/^([^\d]+)([\d\s.,]+)(.*)$/);
      if (split && split[1].trim().length > 1) {
        const prefix = document.createElement('span'); prefix.className = 'currency-prefix'; prefix.textContent = split[1];
        el.replaceChildren(prefix, document.createTextNode(split[2] + split[3])); return;
      }
    }
    el.textContent = label;
  });
  function paintBalance() {
    const show = Boolean(market && claimed());
    buttons().forEach(button => {
      button.hidden = !show;
      if (show) {
        const amount = button.querySelector('[data-rc-balance-amount]'); if (amount) amount.textContent = format(market.welcome);
        button.setAttribute('aria-label', 'View your ' + format(market.welcome) + ' store credit');
      }
    });
    document.dispatchEvent(new CustomEvent('rosie:welcome:state'));
  }
  function showStep(name) {
    Object.entries(steps).forEach(([key, el]) => { if (el) el.hidden = key !== name; });
    status.textContent = '';
    dialog.setAttribute('aria-labelledby', 'rc-' + name + '-title');
  }
  function announce(text) {
    live.textContent = text;
    const toast = q('[data-credit-toast]'); toast.textContent = text; toast.hidden = false;
    clearTimeout(toastTimer); toastTimer = setTimeout(() => { toast.hidden = true; }, 3500);
  }
  function open(name = 'signup') {
    if (!market) return;
    if (name === 'signup' && !signupReady) return;
    clearTimeout(autoTimer); previousFocus = document.activeElement;
    showStep(name); paintAmount();
    q('[data-credit-country]').textContent = countryName || '';
    q('[data-credit-currency]').textContent = currency + ' credit';
    if (name === 'balance') {
      q('[data-credit-balance-copy]').textContent = bonusOn
        ? 'Your welcome credit is applied to your bag automatically. Add a second item for an extra ' + format(market.bonus) + ' off.'
        : 'Your welcome credit is applied to your bag automatically, as a one-use discount.';
    }
    if (!dialog.open) dialog.showModal();
    dialog.focus({ preventScroll: true }); // focus the dialog, not the field: don't force the phone keyboard open
  }
  function close() {
    clearTimeout(autoTimer);
    if (!claimed()) { state.dismissedAt = Date.now(); save(); }
    if (dialog.open) dialog.close();
    if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
  }
  function shouldAutoOpen() {
    if (!signupReady || claimed() || dismissedRecently()) return false;
    if (preview) return true;
    if (['cart', 'password', 'gift_card'].includes(config.pageType)) return false;
    if (config.trigger.scope === 'product' && config.pageType !== 'product') return false;
    if (config.trigger.adsOnly && !isAdVisitor()) return false;
    return true;
  }
  function schedule() {
    if (!shouldAutoOpen()) return;
    const whenClear = () => {
      if (document.querySelector('dialog[open], .hbr-cart-drawer.is-open')) { autoTimer = setTimeout(whenClear, 800); return; }
      if (shouldAutoOpen()) open('signup');
    };
    autoTimer = setTimeout(whenClear, Math.max(0, Number(config.trigger.delay) || 3) * 1000);
  }

  // Claim = Klaviyo accepted the signup. From here on the reconciler keeps the codes on the cart.
  async function claim() {
    state.claimedAt = Date.now(); save(); paintBalance(); paintCards();
    try { window.Shopify?.analytics?.publish?.('rosie_welcome_credit_claimed', { currency, amount: market.welcome, country }); } catch { /* optional */ }
    let result = 'saved';
    if (!preview) {
      try {
        await serial(async () => { const cart = await cartRequest('cart.js'); accept(cart); await reconcile(cart); });
        const entry = snapshot?.codes.find(item => lower(item.code) === lower(config.codes.welcome));
        result = !snapshot || snapshot.count === 0 ? 'saved' : entry && entry.applicable !== false ? 'applied' : 'failed';
      } catch { result = 'saved'; }
    } else { result = 'applied'; scheduleSync(0); }
    const copy = {
      applied: 'Applied to your bag. No minimum spend, one-use discount.',
      saved: 'Saved for you. Add something lovely to your bag and it’s applied automatically.',
      failed: 'We couldn’t apply it automatically. Enter code ' + config.codes.welcome + ' at checkout.'
    }[result];
    setText(q('[data-credit-apply-status]'), preview ? 'Design preview only. No real discount is applied.' : copy);
  }

  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || !market) return;
    email.value = email.value.trim();
    if (!email.validity.valid) { setStatus('Please enter a valid email address.'); email.focus(); return; }
    if (!consent.checked) { setStatus('Please choose to receive Rosie’s emails to claim your credit.'); consent.focus(); return; }
    busy = true;
    const submit = q('[data-credit-submit]'); submit.disabled = true; submit.setAttribute('aria-busy', 'true');
    setStatus('Saving your subscription…');
    try {
      if (!preview) {
        await klaviyo.subscribe(config.klaviyo, { email: email.value, consent: true, campaign: config.campaign, country, currency, offerAmount: market.welcome, code: config.codes.welcome, test: false });
      }
      await claim();
      email.value = ''; showStep('success'); paintAmount();
      dialog.focus({ preventScroll: true });
    } catch (error) {
      setStatus(error.userMessage || 'We couldn’t save your signup. Please try again.');
    } finally { busy = false; submit.disabled = false; submit.removeAttribute('aria-busy'); }
  });
  qa('[data-credit-close]').forEach(el => el.addEventListener('click', close));
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close();
  });
  q('[data-credit-continue]').addEventListener('click', async () => {
    const from = q('[data-credit-success] .credit-value').getBoundingClientRect();
    close(); paintBalance();
    const target = buttons().find(el => el.getClientRects().length > 0);
    if (!target) return;
    target.focus({ preventScroll: true });
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches && Element.prototype.animate) {
      const to = target.getBoundingClientRect(), chip = document.createElement('span');
      chip.className = 'rc-credit-flight'; chip.textContent = '+' + format(market.welcome); chip.setAttribute('aria-hidden', 'true');
      chip.style.left = from.left + 'px'; chip.style.top = from.top + 'px'; document.body.append(chip);
      const animation = chip.animate([
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: 'translate(' + (to.left - from.left) + 'px,' + (to.top - from.top) + 'px) scale(.65)', opacity: 0 }
      ], { duration: 650, easing: 'cubic-bezier(.4,0,.2,1)' });
      try { await animation.finished; } catch { /* animation cancelled */ }
      chip.remove();
    }
    announce('Your ' + format(market.welcome) + ' credit is waiting by your bag.');
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-rc-balance]');
    if (button && claimed()) { event.preventDefault(); open('balance'); }
  });
  document.addEventListener('rosie:credit:open', () => open(claimed() ? 'balance' : 'signup'));
  document.addEventListener('submit', event => { if (event.target.matches('form[action*="/localization"]')) close(); });
  document.addEventListener('shopify:section:load', paintBalance);
  const fit = () => root.style.setProperty('--rc-visible-height', (window.visualViewport?.height || innerHeight) + 'px');
  fit(); window.visualViewport?.addEventListener('resize', fit);
  window.addEventListener('pagehide', () => clearTimeout(autoTimer));

  /* ---------- preview tools (theme editor / unpublished preview only) ---------- */
  const tools = q('[data-credit-preview-tools]');
  if (tools) tools.hidden = !preview;
  q('[data-credit-preview-note]').hidden = !preview;
  if (preview) {
    if (!market) {
      const message = 'No amount is set for ' + (currency || 'this currency') + '. Add it in Theme settings → Rosie welcome credit.';
      q('[data-credit-preview-note]').textContent = message;
      const label = q('[data-credit-preview-status]'); if (label) label.textContent = message;
      const replayButton = q('[data-credit-replay]'); if (replayButton) replayButton.disabled = true;
    }
    q('[data-credit-replay]')?.addEventListener('click', () => {
      if (busy) return;
      state = {}; save(); form.reset(); paintBalance(); paintCards(); open('signup');
    });
  }

  window.RosieCredit = Object.freeze({ format, market: () => market, open, claimed, canSignup: () => signupReady });
  paintBalance();
  if (claimed()) scheduleSync(0);
  if (editor || forcedPreview) { if (signupReady) open('signup'); } else schedule();
})();
